import type { Tour } from '../tour-types';

/**
 * Experiments → Learnings. One route, and what shows on it follows the state: setup until learnings
 * are on, waiting until the first learning is written, then three views (a menu on the crumb bar) and two pages under them.
 * LearningsScreen reports each as a screen (useTourScreen), so each gets a tour of its own.
 */
const ROUTES = ['/experiments/learnings'];

export const learningsSetupTour: Tour = {
  key: 'learnings.setup',
  version: 1,
  routes: ROUTES,
  screen: 'setup',
  steps: [
    {
      anchor: 'learnings-foundation',
      placement: 'top',
      title: 'Foundation check',
      body: 'What each finished test has written down. A clear hypothesis, an expected mechanism and a debrief make sharper learnings, so fix the gaps first if you can.',
    },
    {
      anchor: 'learnings-write',
      placement: 'top',
      requires: 'learnings:manage',
      title: 'Write learnings',
      body: 'Turns learnings on for this website and writes them for its past tests. It uses AI credits, and the estimate is shown before you start.',
    },
  ],
};

export const learningsWaitingTour: Tour = {
  key: 'learnings.waiting',
  version: 1,
  routes: ROUTES,
  screen: 'waiting',
  steps: [
    {
      anchor: 'learnings-queue',
      placement: 'top',
      title: 'In the queue',
      body: 'The tests being read, a few at a time. You can leave this page; learnings appear here when the first ones are written.',
    },
  ],
};

export const learningsTour: Tour = {
  key: 'learnings',
  version: 1,
  routes: ROUTES,
  screen: 'learnings',
  steps: [
    {
      anchor: 'learnings-tabs',
      placement: 'bottom',
      title: 'Three views',
      body: 'Switch between the learnings, the evidence behind them and the opportunities they point to.',
    },
    {
      anchor: 'learnings-ask',
      placement: 'bottom',
      title: 'Ask about your learnings',
      body: 'Ask in your own words, from any view. Answers come only from this website’s tests, and the conversation is shared with your team.',
    },
    {
      anchor: 'learnings-hero',
      placement: 'bottom',
      title: 'What it adds up to',
      body: 'The most useful thing your tests show right now, with the counts behind it.',
    },
    {
      anchor: 'learnings-key',
      placement: 'top',
      title: 'Key learnings',
      body: 'The few things most worth acting on: a surprise to explain, a lead to confirm, a pattern to stop.',
    },
    {
      anchor: 'learnings-map',
      placement: 'top',
      title: 'Evidence map',
      body: 'Every pattern on one axis. The dot is the estimate and the band is where the true effect most likely sits, widened because many patterns are checked at once.',
    },
    {
      anchor: 'learnings-coverage',
      placement: 'left',
      title: 'Coverage',
      body: 'How many finished tests are read, and how many are left out or thin on detail.',
    },
  ],
};

export const learningsEvidenceTour: Tour = {
  key: 'learnings.evidence',
  version: 1,
  routes: ROUTES,
  screen: 'evidence',
  steps: [
    {
      anchor: 'learnings-grid',
      placement: 'top',
      title: 'Page by change type',
      body: 'The pooled lift of every kind of change on every page. A dashed cell is one test; an empty one has never been tried.',
    },
    {
      anchor: 'learnings-not-in-evidence',
      placement: 'top',
      title: 'Not in the evidence',
      body: 'Tests still being read, and the ones left out because their data cannot be trusted.',
    },
  ],
};

export const learningsOpportunitiesTour: Tour = {
  key: 'learnings.opportunities',
  version: 1,
  routes: ROUTES,
  screen: 'opportunities',
  steps: [
    {
      anchor: 'learnings-opportunity',
      placement: 'right',
      title: 'An open question',
      body: 'Each one is a question your evidence raises, with the experiment that would answer it. Draft experiment opens it pre-filled.',
    },
    {
      anchor: 'learnings-untested',
      placement: 'left',
      title: 'Untested combinations',
      body: 'Kinds of change tried on some pages and never on others.',
    },
  ],
};

export const learningsPatternTour: Tour = {
  key: 'learnings.pattern',
  version: 1,
  routes: ROUTES,
  screen: 'pattern',
  steps: [
    {
      anchor: 'learnings-pattern-head',
      placement: 'bottom',
      title: 'One learning',
      body: 'Mark it as known to move it down, or not useful to hide it. Neither raises new questions after that.',
    },
    {
      anchor: 'learnings-estimate',
      placement: 'top',
      title: 'Estimate',
      body: 'The pooled estimate and each test behind it. Where the band crosses zero, the opposite effect is still possible.',
    },
    {
      anchor: 'learnings-contributing',
      placement: 'top',
      title: 'Contributing tests',
      body: 'The tests this learning rests on. Open a debrief to see what each one changed and taught.',
    },
  ],
};

export const learningsTestTour: Tour = {
  key: 'learnings.test',
  version: 1,
  routes: ROUTES,
  screen: 'test',
  steps: [
    {
      anchor: 'learnings-taught',
      placement: 'top',
      title: 'What it taught',
      body: 'The learning written by AI, with a possible reason and what to try next. Mark it reviewed once someone has read it.',
    },
    {
      anchor: 'learnings-test-foundation',
      placement: 'left',
      title: 'Foundation',
      body: 'What this test has written down. Filling a gap makes its learning sharper next time it is read.',
    },
  ],
};

export const LEARNINGS_TOURS: readonly Tour[] = [
  learningsSetupTour,
  learningsWaitingTour,
  learningsTour,
  learningsEvidenceTour,
  learningsOpportunitiesTour,
  learningsPatternTour,
  learningsTestTour,
];
